// Repositorio de standby assignments. v1 (core-manage-api-v1.json:28850+, tag `Standby`) rediseñó
// el recurso por completo — DTO 100% camelCase (`StandbyAssignmentDto`), se convierte en el borde
// con `toSnakeCaseKeys`/`toCamelCaseKeys` (mismo patrón `cloudAccount.repository.ts`/
// `changeBatch.repository.ts`), dominio se mantiene snake_case en los DTO internos.
//
// Decisiones de contrato (T61):
// - `getAll({clientId})`: v1 NO tiene `GET /standby-assignments` plano — se usa
//   `GET /clients/{clientId}/standby-assignments` (confirmado v1-endpoints.txt:139,
//   `StandbyAssignmentListDto` → `{ items: [...] }`, `toItemArray`).
// - `getAll({userId})`: única vía es `GET /users/{userId}/standby-assignments` — **hueco
//   heredado de T25/Fase 3**: esa ruta respondió `500` en el backend real probado (tasks.md nota
//   T25). Se implementa igual (es la única opción documentada) pero queda registrado el riesgo.
// - `getAll()` SIN filtros (consumidor: `useStandbyList`, listado global multi-cliente de
//   `StandbyPage`): **hueco sin solución en este repositorio** — v1 no expone ningún listado no
//   acotado por cliente/usuario (ni `/standby-assignments/stats` ni `/export*` sirven, son
//   agregados/binarios). Lanza un error explícito en vez de fallar en silencio; requiere decisión
//   de producto (¿fan-out por cliente en `application/`? ¿nuevo endpoint admin?) fuera de alcance
//   de T61 — no se resuelve aquí para no mezclar migración con diseño nuevo (constitution.md #6).
// - `getUserLevel`: se reemplaza `GET /standby-assignments/user-level/{userId}` (ruta muerta en
//   v1) por `GET /users/{userId}/standby-context` (v1-endpoints.txt:282,
//   `StandbyAssignmentPlanningContextDto.establishedEscalationLevel` es exactamente el nivel por
//   defecto que UC-S6 necesita).
// - `create`/`bulkCreate`: mismas rutas (`POST /standby-assignments`, `POST
//   /standby-assignments/bulk`); campos de escritura renombrados en el wire: `start_time`/
//   `end_time`/`assignment_notes` legado → `startAt`/`endAt`/`notes` (StandbyAssignmentDto no
//   tiene los nombres viejos).
// - `update`: v1 solo expone `PATCH` (no PUT, confirmado core-manage-api-v1.json:29708+). Además,
//   `StandbyAssignmentDto` YA NO tiene un campo `isActive` escribible — el estado activo/inactivo
//   vive en `status` (enum, solo lectura vía PATCH normal) y el toggle tiene su propio endpoint
//   dedicado `POST /standby-assignments/{id}/toggle` (core-manage-api-v1.json:29561+). Cuando
//   `isActive` es el ÚNICO campo del `partial` (patrón real de `useStandbyMutations`:
//   `update(id, { isActive: !record.isActive })`) se usa el toggle; si se combina con otros
//   campos no hay endpoint que haga ambas cosas a la vez (sin consumidor real de esa combinación).
// - `copyWeek`: de las dos rutas candidatas (`week-copy-draft` vs `weekly-copy`,
//   v1-endpoints.txt:475-476), se usa `POST /standby-assignments/week-copy-draft` — el legado
//   `copyWeek` es una PREVISUALIZACIÓN (llena `rows` para revisión/edición en el bulk dialog; la
//   persistencia real ocurre en un `submit()` aparte que llama a `bulkCreate`), lo que corresponde
//   semánticamente a un "draft", no a un commit directo. Body sin schema documentado (`JsonObject`)
//   — mejor esfuerzo en camelCase (mismo criterio G-10 de `cloudAccount.repository.ts`). **Hueco**:
//   la respuesta (`items: {sourceAssignmentId, userId, platformId, startAt, endAt,
//   escalationLevel, notes, isActive}`) YA NO incluye `username`/`firstName`/`lastName`/`phone`/
//   `platformName` (el join de usuario/plataforma que el bulk dialog mostraba de entrada) — se
//   rellenan con `''`/`undefined`; el dialog sigue funcionando (usuario/plataforma quedan
//   seleccionados por id) pero no muestra el nombre hasta que se recarga `platformUsersCache`.
// - `StandbyAssignmentDto.userDisplayName` (nombre combinado) reemplaza los `first_name`/
//   `last_name`/`username` separados del DTO legado — se mapea el valor completo a `firstName`
//   (mejor esfuerzo para no dejar la fila sin ningún nombre visible), `lastName`/`username`/
//   `phone` quedan `undefined` (huecos, sin equivalente en v1).
// - `delete`: sin cambios de ruta (`DELETE /standby-assignments/{id}`, ahora 204 No Content).
//
// Expone SOLO los métodos que workforce consume (spec.md §4): getAll, getUserLevel, create,
// bulkCreate, copyWeek, update, delete. NO porta getByUser/getById/getStats/exportReport (§7-D:
// sin consumidor en workforce, sobreviven en src/api/standbyAssignments.api.js).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toCamelCaseKeys, toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type {
  EscalationLevel,
  CopyWeekRow,
  StandbyAssignment,
  StandbyUserLevel,
  StandbyWritePayload,
} from '../../domain/standby/standby.types';
import type { ClientId, PlatformId, StandbyAssignmentId, UserId } from '../../domain/ids';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

const StandbyAssignmentDtoSchema = z.object({
  id: IdSchema,
  user_id: IdSchema,
  client_id: IdSchema.nullish(),
  client_name: z.string().nullish(),
  user_display_name: z.string().nullish(),
  platform_id: IdSchema.nullish(),
  platform_name: z.string().nullish(),
  start_at: z.string().nullish(),
  end_at: z.string().nullish(),
  escalation_level: z.number().nullish(),
  status: z.string().nullish(),
  notes: z.string().nullish(),
  shift_summary: z.string().nullish(),
  computed_status: z.string(),
});
type StandbyAssignmentDto = z.infer<typeof StandbyAssignmentDtoSchema>;

const StandbyUserLevelDtoSchema = z.object({ established_escalation_level: z.number().nullish() });

const CopyWeekRowDtoSchema = z.object({
  user_id: IdSchema,
  platform_id: IdSchema.nullish(),
  escalation_level: z.number().nullish(),
  notes: z.string().nullish(),
  start_at: z.string().nullish(),
  end_at: z.string().nullish(),
});

function toDomain(dto: StandbyAssignmentDto): StandbyAssignment {
  return {
    id: dto.id as StandbyAssignmentId,
    userId: dto.user_id as UserId,
    clientId: (dto.client_id ?? '') as ClientId,
    platformId: (dto.platform_id ?? '') as PlatformId,
    escalationLevel: (dto.escalation_level ?? null) as EscalationLevel | null,
    startTime: dto.start_at ?? '',
    endTime: dto.end_at ?? '',
    assignmentNotes: dto.notes ?? null,
    shiftSummary: dto.shift_summary ?? null,
    computedStatus: dto.computed_status,
    isActive: dto.status ? dto.status === 'active' : undefined,
    // hueco: v1 solo expone `userDisplayName` (nombre combinado) — se mapea a `firstName` para no
    // perder toda identificación visual; `lastName`/`username` no tienen equivalente.
    firstName: dto.user_display_name ?? undefined,
    lastName: undefined,
    username: undefined,
    clientName: dto.client_name ?? undefined,
    platformName: dto.platform_name ?? undefined,
    phone: undefined, // hueco: StandbyAssignmentDto v1 no incluye phone
  };
}

function toDto(payload: StandbyWritePayload) {
  return {
    user_id: payload.userId,
    client_id: payload.clientId,
    platform_id: payload.platformId,
    escalation_level: payload.escalationLevel,
    start_at: payload.startTime,
    end_at: payload.endTime,
    notes: payload.assignmentNotes,
  };
}

export interface StandbyAssignmentQuery {
  userId?: UserId;
  clientId?: ClientId;
}

export const standbyAssignmentRepository = {
  async getAll(query: StandbyAssignmentQuery = {}): Promise<StandbyAssignment[]> {
    if (query.clientId) {
      const { data } = await apiClient.get(`/clients/${query.clientId}/standby-assignments`);
      return z
        .array(StandbyAssignmentDtoSchema)
        .parse(toSnakeCaseKeys(toItemArray(data)))
        .map(toDomain);
    }
    if (query.userId) {
      // hueco heredado T25: esta ruta respondió 500 en el backend real probado; es la única vía
      // documentada para un listado por usuario, se implementa igual (ver cabecera del archivo).
      const { data } = await apiClient.get(`/users/${query.userId}/standby-assignments`);
      return z
        .array(StandbyAssignmentDtoSchema)
        .parse(toSnakeCaseKeys(toItemArray(data)))
        .map(toDomain);
    }
    // hueco: v1 no tiene listado no acotado por cliente/usuario (ver cabecera) — falla explícito
    // en vez de devolver datos incompletos en silencio.
    throw new Error(
      'standbyAssignmentRepository.getAll(): v1 no expone un listado plano de standby-assignments; se requiere clientId o userId (ver cabecera de standbyAssignment.repository.ts)'
    );
  },

  async getUserLevel(userId: UserId): Promise<StandbyUserLevel> {
    const { data } = await apiClient.get(`/users/${userId}/standby-context`);
    const dto = StandbyUserLevelDtoSchema.parse(toSnakeCaseKeys(data ?? {}));
    return { escalationLevel: (dto.established_escalation_level ?? null) as EscalationLevel | null };
  },

  async create(payload: StandbyWritePayload): Promise<StandbyAssignment> {
    const { data } = await apiClient.post('/standby-assignments', toCamelCaseKeys(toDto(payload)));
    return toDomain(StandbyAssignmentDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // La UI solo muestra el count del resultado (spec.md UC-B1) — retorno mínimo { created }.
  async bulkCreate(payloads: StandbyWritePayload[]): Promise<{ created: number }> {
    const body = toCamelCaseKeys({ assignments: payloads.map(toDto) });
    const { data } = await apiClient.post('/standby-assignments/bulk', body);
    const raw = toSnakeCaseKeys(data ?? {}) as { created?: number };
    return { created: Number(raw.created ?? 0) };
  },

  async copyWeek(params: {
    clientId: ClientId;
    sourceStart: string;
    sourceEnd: string;
    targetStart: string;
  }): Promise<CopyWeekRow[]> {
    const body = toCamelCaseKeys({
      client_id: params.clientId,
      source_start: params.sourceStart,
      source_end: params.sourceEnd,
      target_start: params.targetStart,
    });
    const { data } = await apiClient.post('/standby-assignments/week-copy-draft', body);
    return z
      .array(CopyWeekRowDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(
        (dto): CopyWeekRow => ({
          userId: dto.user_id as UserId,
          platformId: (dto.platform_id ?? '') as PlatformId,
          escalationLevel: (dto.escalation_level ?? null) as EscalationLevel | null,
          assignmentNotes: dto.notes ?? null,
          startTime: dto.start_at ?? '',
          endTime: dto.end_at ?? '',
          // hueco: week-copy-draft no incluye join de usuario/plataforma (ver cabecera).
          username: '',
          firstName: '',
          lastName: '',
          phone: undefined,
          platformName: undefined,
        })
      );
  },

  // Update parcial. Además de `shiftSummary` (único campo usado por workforce, spec.md UC-S6),
  // acepta el resto de campos de `StandbyWritePayload` + `isActive` (T106, handoff N-2: edición
  // completa y toggle de `isActive` consumidos por `clients` vía `useStandbyMutations`). Cuando
  // `isActive` es el único campo presente se usa el toggle dedicado (ver cabecera); en cualquier
  // otro caso se hace `PATCH` solo con las claves presentes en `partial` (paridad con el update
  // parcial legado). Deja pasar el error del backend tal cual (patrón rack.repository.ts) — su
  // mapeo a mensaje legible vive en application/.
  async update(
    id: StandbyAssignmentId,
    partial: Partial<StandbyWritePayload> & { shiftSummary?: string; isActive?: boolean }
  ): Promise<StandbyAssignment> {
    const { isActive, shiftSummary, userId, clientId, platformId, escalationLevel, startTime, endTime, assignmentNotes } =
      partial;
    const hasOtherFields = [
      shiftSummary,
      userId,
      clientId,
      platformId,
      escalationLevel,
      startTime,
      endTime,
      assignmentNotes,
    ].some((value) => value !== undefined);

    if (isActive !== undefined && !hasOtherFields) {
      const { data } = await apiClient.post(`/standby-assignments/${id}/toggle`);
      return toDomain(StandbyAssignmentDtoSchema.parse(toSnakeCaseKeys(data)));
    }

    const dto: Record<string, unknown> = {};
    if (shiftSummary !== undefined) dto.shift_summary = shiftSummary;
    if (userId !== undefined) dto.user_id = userId;
    if (clientId !== undefined) dto.client_id = clientId;
    if (platformId !== undefined) dto.platform_id = platformId;
    if (escalationLevel !== undefined) dto.escalation_level = escalationLevel;
    if (startTime !== undefined) dto.start_at = startTime;
    if (endTime !== undefined) dto.end_at = endTime;
    if (assignmentNotes !== undefined) dto.notes = assignmentNotes;
    const { data } = await apiClient.patch(`/standby-assignments/${id}`, toCamelCaseKeys(dto));
    return toDomain(StandbyAssignmentDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Portado en T106 (plan.md §7-I): eliminar un standby individual, consumido por `clients` vía
  // `useStandbyMutations`. Sin body de retorno (204 No Content en v1, paridad de comportamiento).
  async delete(id: StandbyAssignmentId): Promise<void> {
    await apiClient.delete(`/standby-assignments/${id}`);
  },
};
